
/**
 * Formats a date as MM/DD/YYYY (UTC).
 * @param date - Date value (Date, string, or number)
 * @returns Formatted date string (e.g. "02/06/2025")
 */
export const formatedDate = (date: any) => {
  date = new Date(date);
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const year = date.getUTCFullYear();

  return `${month}/${day}/${year}`;
};

/**
 * Clears localStorage asynchronously.
 * @returns Promise that resolves when clear succeeds, or rejects on error
 */
export function clearLocalStorageAsync() {
  return new Promise((resolve:any, reject:any) => {
    try {
      localStorage.clear();
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Checks if a string contains only digits and at most one decimal point.
 * @param value - String to validate
 * @returns True if value matches decimal/number pattern, false otherwise
 */
export const acceptDecimal = (value: string) => {
  const regex = /^[0-9]*\.?[0-9]*$/; // regex to allow decimal and numbers only
  if (regex.test(value)) {
    return true
  }
  return false;
}

/**
 * Measurement masking placeholder; currently returns input unchanged.
 * @param input - String to mask
 * @returns The input string (no masking applied)
 */
export const maskInput = (input: string) => {
  return input;
}

/**
 * Maps a list of items to autocomplete options (label/value).
 * @param list - Array of objects with `title` and `id`
 * @returns Array of { label: item.title, value: item.id }
 */
export const modifyListForAutocomplete = (list: any | []) => {
  const result = list.map((item: any) => {
    return {
      label: item?.title,
      value: item?.id
    }
  });
  return result;
}

/**
 * Truncates a number to the given precision without rounding.
 * @param number - Numeric value
 * @param precision - Number of decimal places
 * @returns Fixed-point string (e.g. "12.34")
 */
const toFixedNoRound = (number:any, precision:any) =>{
  if(number%1 === 0){
    return number.toFixed(precision);
  }
  const factor = Math.pow(10, precision);
  const num = Math.floor(Number((number * factor).toFixed(0)))/factor
  return num.toFixed(precision)
}

/**
 * Formats a value as a percentage with thousands separators; preserves leading minus.
 * @param input - Number or string to format
 * @param precision - Decimal places (default: 2)
 * @returns Formatted string (e.g. "1,234.56 %" or "-1,234.56%")
 */
export const percentMask = (input: any, precision?: number) => {
  let valSign = '';
  const amount = input === 0 ? "0.00" : input === null ? "0.00" : typeof input === "string" ? input : toFixedNoRound(input, precision ? precision : 2);

  // Extract '-' sign if it has been entered.
  valSign = amount?.slice(0, 1) === '-' ? amount.slice(0, 1) : '';

  if (valSign) {
    return `${valSign}${amount.toString().slice(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}%`;
  }
  return `${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} %`;

}

/**
 * Formats a value as a currency/amount string with thousands separators and optional sign.
 * @param input - Number or string to format
 * @param currencySign - Optional prefix (e.g. "$", "€")
 * @param precision - Decimal places (default: 2)
 * @returns Formatted string (e.g. "$1,234.56" or "-$1,234.56")
 */
export const amountMask = (input: any, currencySign?: string, precision?: number) => {
  let valSign = '';
  const amount = input === 0 ? "0.00" : input === null ? "0.00" : typeof input === "string" ? input : toFixedNoRound(input, precision? precision: 2);
  // extract '-' sign if it has been entered.
  valSign = amount?.slice(0, 1) === '-' ? amount.slice(0, 1): '';
  if(valSign){
    return `${valSign}${currencySign ?? ''}${amount.toString().slice(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }
  return `${valSign}${currencySign ?? ''}${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

/**
 * Computes chapter cost as unitCost × quantity and returns it formatted via amountMask.
 * @param unitCost - Cost per unit
 * @param quantity - Number of units
 * @returns Formatted amount string (e.g. "1,234.56")
 */
export const chapterCost = (unitCost: number, quantity: number) => {
  const result: any = unitCost * quantity
  return amountMask(result?.toFixed(2))
}

/**
 * Calculates owner price from unit cost, quantity, and markup (percent or fixed).
 * @param unitCost - Cost per unit
 * @param quantity - Number of units
 * @param markup - Markup value (percent or fixed amount)
 * @param markupType - "%" for percentage markup, else treated as fixed amount
 * @returns Owner price as fixed 2-decimal string
 */
export const calculateOwnerPrice = (unitCost: number, quantity: number, markup: number, markupType: any) => {
  const chapterCost = unitCost * quantity
  let result: any = 0;

  if (markupType === "%") {
      if (markup === 0 || isNaN(markup)) {
          result = chapterCost;
      } else {
          result = (chapterCost * (markup / 100)) + chapterCost;
      }
  } else {
      if (isNaN(markup)) {
          result = chapterCost;
      } else {
          result = Number(chapterCost) + Number(markup);
      }
  }
  return result.toFixed(2)
  // return amountMask(result.toFixed(2));
}

/**
 * Sums owner price (unitCost × quantity + markup) for all subcategories in a group.
 * @param array - Array of groups, each with subcategories (unitCost, quantity, markup, markupType)
 * @param groupIndex - Index of the group to total
 * @returns Formatted total amount string via amountMask
 */
export const calculateNonItemizedTotalCost = (array: any, groupIndex: any) => {
  let totalOwnerPrice = 0;

  const firstGroup = array[groupIndex]; // Get the first group

  firstGroup.subcategories.forEach((item: any) => {
          const chapterCost = parseFloat(item.unitCost) * parseFloat(item.quantity);
          const ownerPrice =
              item.markupType === "%"
                  ? chapterCost * (item.markup / 100) + chapterCost
                  : Number(chapterCost) + Number(item.markup);
          totalOwnerPrice += ownerPrice || 0;
  });

  return amountMask(totalOwnerPrice.toFixed(2));
};

/**
 * Extracts initials from a full name (first letter of first two words, space-separated).
 * @param name - Full name string
 * @returns Two initials with a space (e.g. "J D"), or "A A" if name is null/undefined
 */
export const getInitials = (name: string) => {
  let init1;
  let init2;
  if (name === null || name === undefined) {
    init1 = "A";
    init2 = "A";
  } else {
    init1 = name?.split(' ')[0].slice(0, 1) || '';
    init2 = name?.split(' ')[1].slice(0, 1) || '';
  }
  return `${init1} ${init2}`;
}

/**
 * Safe initials from a string; accepts undefined/any, returns uppercase two letters without space.
 * @param value - Name string (or any value; non-strings are treated as empty)
 * @returns Uppercase initials (e.g. "JD") or empty string if no valid name
 */
export const getInitialsSafe = (value?: any) => {
  const safe = (typeof value === 'string' ? value : '').trim();
  if (!safe) return '';
  const parts = safe.split(/\s+/);
  const first = parts[0]?.charAt(0) || '';
  const second = parts[1]?.charAt(0) || '';
  return `${first}${second}`.toUpperCase();
}

/**
 * Masks raw input as length in feet'inches" format (e.g. 5'06") while typing.
 * @param value - Raw input string
 * @returns Formatted length string with ' and " placeholders
 */
export const maskLength = (value: any) => {
  let inputValue;
  inputValue = value.replace(/\D/g, "")
    .replace(/(\d{2})/, `$1'`)
    .replace(/(\d{2})'([23456789])/, `$1'0$2`)
    .replace(/'(\d{2})/, `'$1"`)
    // .replace(/(\d{2})'([23456789])/, `$1'1`)
    .replace(/('[1])([23456789])"/, `$1`)
    // .replace(/('[0])(\d)/, `$1$2"`)
    .replace(/('[1])([01])/, `$1$2`)
    .replace(/('\d{2})"(\d)/, `$1"`)
    .replace(/("\d)/, `"`)

  return inputValue;
}

/**
 * Normalizes length input on blur to feet'inches" (e.g. 5'06"); pads missing digits with 0.
 * @param value - Current length string (possibly partial)
 * @returns Normalized length string (e.g. "05'00"", "12'06"")
 */
export const maskLengthOnBlur = (value: any) => {
  let inputValue;
  if (value?.length < 2) {
    inputValue = value
      .replace(/\D/g, "")
      .replace(/(\d{1})/, `0$1'00"`);
  } else if (value?.length < 4) {
    inputValue = value
      .replace(/\D/g, "")
      .replace(/(\d{2})/, `$1'00"`);
  } else if (value?.length < 5) {
    if (/(\d{1})'(\d{2})/.test(value)) {
      inputValue = value
        .replace(/(\d{1})'(\d{2})/, `0$1'$2"`)
    } else {
      inputValue = value
        .replace(/(\d{2})'(\d{1})/, `$1'0$2"`)
    }
  } else {
    inputValue = value
  }
  return inputValue;
}

/**
 * Compares two URLs for structural equality, treating UUIDs and MongoDB ObjectIDs as placeholders.
 * @param url1 - First URL
 * @param url2 - Second URL (compared literally; typically one is already normalized)
 * @returns True if URLs match after replacing IDs with a placeholder
 */
const compareUrls = (url1:string, url2:string) => {

  function markUUIDsInURL(url:string) {
      // Regular expression to match UUIDs
      const uuidRegex = /[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}/g;
      // Replace UUIDs with "<UUID>"
      let markedURL = url.replace(uuidRegex, "<UUID>");    
      // Regular expression to match Mongoose ObjectIDs
      const objectIdRegex = /[0-9a-fA-F]{24}/g;
      // Replace ObjectIDs with "<ID>"
      markedURL = markedURL.replace(objectIdRegex, (match) => {
          // Check if the match is a valid ObjectID
          if (/^[0-9a-fA-F]{24}$/.test(match)) {
              return "<UUID>";
          }
          return match; // Return the original match if not a valid ObjectID
      });
      return markedURL;
  }

  // Remove UUIDs from both URLs
  const cleanUrl1 = markUUIDsInURL(url1);
  const cleanUrl2 = (url2);

  // Compare the modified URLs
  return cleanUrl1 === cleanUrl2;
}

/**
 * Checks whether targetUrl is structurally equal to any URL in the list (IDs normalized).
 * @param targetUrl - URL to look for
 * @param urlList - List of URLs to compare against
 * @returns True if any list URL matches targetUrl (ignoring UUID/ObjectID differences)
 */
export const containsSimilarUrl = (targetUrl:string, urlList:string[]):boolean => {
    for (let i = 0; i < urlList.length; i++) {
        if (compareUrls(targetUrl, urlList[i])) {
            return true;
        }
    }
    return false;
}

/**
 * Returns ellipsized text and the original text.
 * @param text - Original string to ellipsize
 * @param maxLength - Max character length before appending ellipsis (ellipsis length is included in this)
 * @param ellipsis - String to append when truncated (default: '...')
 * @returns Object with `ellipsized` (truncated string) and `original` (unchanged input)
 */
export const getEllipsizedText = (
  text: string,
  maxLength: number,
  ellipsis = '...'
): { ellipsized: string; original: string } => {
  const original = text ?? '';
  if (original.length <= maxLength) {
    return { ellipsized: original, original };
  }
  const cutAt = Math.max(0, maxLength - ellipsis.length);
  return {
    ellipsized: `${original.slice(0, cutAt)}${ellipsis}`,
    original,
  };
};

/**
 * Stores a token in localStorage under the given name and removes it after 9 minutes.
 * @param token - Token value to store
 * @param tokenName - localStorage key (e.g. "authToken")
 */
export const setTokenWithExpiry = (token: any, tokenName: string) => {
  localStorage.setItem(tokenName, token);
  setTimeout(() => {
    let nameHolder = JSON.parse(JSON.stringify(tokenName))
    localStorage.removeItem(nameHolder);
  }, 9 * 60 * 1000);
}